import React, { useState, useRef, useEffect } from "react";
import LottieIcon from "../common/LottieIcon";
import { getIconCategoryClass } from "../common/iconCategory";

const LibraryPanel = ({ libraryItems, onDragStart, onDragEnd }) => {
  const [activeCategory, setActiveCategory] = useState('Features');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const categoriesRef = useRef(null);
  
  const CATEGORY_ORDER = ['Campaign', 'Features', 'Arcade/IRL', 'Effects', 'Core', 'Visual Components'];
  const allCategories = Object.keys(libraryItems);
  const categories = [
    ...CATEGORY_ORDER.filter(c => allCategories.includes(c)),
    ...allCategories.filter(c => !CATEGORY_ORDER.includes(c) && c !== 'Misc'),
    ...( allCategories.includes('Misc') ? ['Misc'] : [] ),
  ];
  
  // Set active category to first available when items load
  useEffect(() => {
    if (categories.length > 0 && !categories.includes(activeCategory)) {
      setActiveCategory(categories[0]);
    }
  }, [categories]);
  
  const updateScrollButtons = () => {
    if (categoriesRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = categoriesRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };
  
  useEffect(() => {
    updateScrollButtons();
    const categoriesEl = categoriesRef.current;
    if (categoriesEl) {
      categoriesEl.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        categoriesEl.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, []);
  
  const scrollCategories = (direction) => {
    if (categoriesRef.current) {
      const scrollAmount = 100;
      categoriesRef.current.scrollLeft += direction === 'right' ? scrollAmount : -scrollAmount;
    }
  };

  // Scroll the active tab into view within the container when it changes
  useEffect(() => {
    const container = categoriesRef.current;
    if (!container) return;
    const activeBtn = container.querySelector('.category-tab.active');
    if (!activeBtn) return;
    activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [activeCategory]);

  return (
    <div className="library-panel">
      {categories.length === 0 ? (
        <div className="library-empty">
          <p>Loading features...</p>
        </div>
      ) : (
        <>
          <div className="library-categories-wrapper">
            <button 
              className="category-scroll-btn" 
              onClick={() => scrollCategories('left')}
              disabled={!canScrollLeft}
              title="Scroll left"
            >
              ‹
            </button>
            <div className="library-categories" ref={categoriesRef}>
              {categories.map((category) => (
                <button
                  key={category}
                  className={`category-tab ${activeCategory === category ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
            <button 
              className="category-scroll-btn" 
              onClick={() => scrollCategories('right')}
              disabled={!canScrollRight}
              title="Scroll right"
            >
              ›
            </button>
          </div>
          
          <div className="library-items-grid">
            {libraryItems[activeCategory]?.map((item) => (
              <div
                key={`${item.id}-${item.label}`}
                className="library-item-card"
                draggable
                onDragStart={(event) => onDragStart && onDragStart(item, event)}
                onDragEnd={(event) => onDragEnd && onDragEnd(event)}
              >
                <div className={`item-image-placeholder category-icon-bg--${getIconCategoryClass(item.iconCategory || item.category)}`}>
                  <LottieIcon icon={item.icon} varKey={item.varKey} size={48} />
                </div>
                <div className="item-name">{item.label}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LibraryPanel;
